import {chain} from 'lodash';
import {DateTime} from 'luxon';
import {connect} from 'react-redux';

import {
  CalendarEventTimeTypesEnum,
  computeCalendarDateTime,
} from '../../../srcCommon/helpers/models/calendarEventVersionHelpers';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../srcCommon/helpers/reselectHelpers';
import {CalendarEventModel} from '../../models/api/calendarEventModel';
import {CalendarEventVersionModel} from '../../models/api/calendarEventVersionModel';
import {CalendarVersionAccessRolesEnum, CalendarVersionModel} from '../../models/api/calendarVersionModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../services/tracking';
import {getSelectedCalendarVersions} from '../../store/data/calendarVersions/calendarVersionsSelectors';
import {
  navigateCalendarsAction,
  openCalendarEventInSidePanelAction,
} from '../../store/panels/calendars/calendarsPanelActions';
import {
  getCalendarSidePanel,
  getIsSidePanelCalendarVisible,
  makeGetCalendarEventData,
  makeGetCalendarsPanel,
} from '../../store/panels/calendars/calendarsPanelSelectors';
import {
  CalendarEventGridParameters,
  CalendarEventParameters,
  FocusedEventSourcesEnum,
} from '../../store/panels/calendars/calendarsPanelState';
import {CalendarsParameters, CalendarsViewTypesEnum} from '../../store/panels/calendars/calendarsParameters';
import {CalendarsPanelId} from '../../store/panels/panelStateBase';
import {inLegacyTransientPanel} from '../legacyTransientPanel';
import {PreviewCalendars} from './previewCalendars';

/*
 * Props.
 */

interface PreviewCalendarsContainerProps {
  panelId: CalendarsPanelId;
  calendarEvent: CalendarEventModel;
  calendarEventVersion: CalendarEventVersionModel;
  calendarVersion: CalendarVersionModel;
}

/*
 * Selectors.
 */

// TODO: Update this to find the correct panel deterministically.
const getSidePanelFocusedDate = createSelector(
  [getCalendarSidePanel],
  (calendarSidePanel) => calendarSidePanel?.parameters.focusedDate,
);

const getSidePanelFocusedCalendarEventParameters = createSelector(
  [getCalendarSidePanel],
  (calendarSidePanel) => calendarSidePanel?.focusedCalendarEventParameters,
);

const getSidePanelFocusedCalendarEventData = makeGetCalendarEventData(
  getSidePanelFocusedCalendarEventParameters,
);

function makeGetFocusedDate() {
  return createSelector([makeGetCalendarsPanel()], (calendarsPanel) => calendarsPanel.parameters.focusedDate);
}

function makeGetCanPreviewEvent() {
  return createSelector(
    [
      getIsSidePanelCalendarVisible,
      getSidePanelFocusedDate,
      getSidePanelFocusedCalendarEventData,
      makeGetFocusedDate(),
    ],
    (isSidePanelCalendarVisible, sidePanelFocusedDate, sidePanelFocusedCalendarEventData, focusedDate) => {
      if (!isSidePanelCalendarVisible || sidePanelFocusedCalendarEventData) {
        return true;
      }

      const sidePanelFocusedDateTime = sidePanelFocusedDate && DateTime.fromMillis(sidePanelFocusedDate);
      if (!sidePanelFocusedDateTime) {
        return false;
      }

      const focusedDateTime = DateTime.fromMillis(focusedDate);
      return !sidePanelFocusedDateTime.hasSame(focusedDateTime, 'day');
    },
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<PreviewCalendarsContainerProps>()(() => ({
  canPreviewEvent: makeGetCanPreviewEvent(),
}));

const mapDispatchToProps = makeMapDispatchToProps<PreviewCalendarsContainerProps>()((props) => ({
  onPreviewClick: () => {
    const {calendarEvent, calendarEventVersion, calendarVersion} = props;
    const focusedDate = computeCalendarDateTime(
      calendarEventVersion,
      CalendarEventTimeTypesEnum.START,
    ).toMillis();

    const instanceStartDate = calendarEvent.recurrence ? focusedDate : undefined;
    const focusedCalendarEventParameters: CalendarEventParameters = {
      calendarEventVersionUid: calendarEventVersion.uid,
      calendarVersionId: calendarVersion.id,
      arbitraryCalendarEventId: undefined,
      instanceStartDate,
    };

    trackInterfaceEvent(InterfaceEventsEnum.CLICK_PREVIEW_EVENT);

    return openCalendarEventInSidePanelAction(focusedDate, focusedCalendarEventParameters);
  },
}));

export const PreviewCalendarsContainer = inLegacyTransientPanel(
  connect(mapStateToProps, mapDispatchToProps)(PreviewCalendars),
  (props) => (dispatch, getState) => {
    const {panelId, calendarEvent, calendarEventVersion, calendarVersion} = props;

    // Find the date to focus.
    const focusedDate = computeCalendarDateTime(
      calendarEventVersion,
      CalendarEventTimeTypesEnum.START,
    ).toMillis();

    // Only display the calendar versions that we own.
    const selectedCalendarVersions = getSelectedCalendarVersions(getState());
    const selectedCalendarVersionIds = chain(selectedCalendarVersions)
      .filter((v) => v.accessRole === CalendarVersionAccessRolesEnum.OWNER)
      .map((v) => v.id)
      .concat(calendarVersion.id) // Add the event's.
      .uniq()
      .value();

    const parameters: Partial<CalendarsParameters> = {
      focusedDate,
      selectedCalendarVersionIds,
      viewType: CalendarsViewTypesEnum.PREVIEW,
    };

    // If this is a recurring event, set the instanceStartDate so that the event is highlighted properly.
    const instanceStartDate = calendarEvent.recurrence ? focusedDate : undefined;
    const focusedCalendarEventParameters: CalendarEventGridParameters = {
      calendarEventVersionUid: calendarEventVersion.uid,
      calendarVersionId: calendarVersion.id,
      arbitraryCalendarEventId: undefined,
      instanceStartDate,
      source: FocusedEventSourcesEnum.CALENDAR_PREVIEW,
      overrides: {},
    };

    dispatch(
      navigateCalendarsAction(panelId, parameters, {
        focusedCalendarEventParameters,
      }),
    );
  },
);
